﻿import cookieApi from '../utils/cookie.js';

const CONSENT_COOKIE = 'catch-consent';
const CONSENT_DURATION = 60 * 60 * 24 * 365;

const DEFAULT_CATEGORIES = {
  necessary: true,
  functional: false,
  analytics: false,
  targeting: false,
};

const CONSENT_DEFAULTS = {
  google: false,
  clarity: false,
  meta: false,
};

const SCRIPT_STATE = {
  metaLoaded: false,
  metaInitialized: false,
  metaLeaveBound: false,
};

function getRoot() {
  return typeof document !== 'undefined' ? document.documentElement : null;
}

function isEmbedMode() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  const root = getRoot();
  if (root?.getAttribute('data-embed') === 'true') {
    return true;
  }

  try {
    const params = new URLSearchParams(window.location.search || '');
    return String(params.get('embed') || '').toLowerCase() === 'true';
  } catch {
    return false;
  }
}

function getPolicyVersion() {
  const root = getRoot();
  return root?.dataset?.cookiePolicyVersion || '1';
}

function resolveGaIdFromScriptTag() {
  if (typeof document === 'undefined') {
    return '';
  }

  const script = document.querySelector('script[src*="googletagmanager.com/gtag/js?id="]');
  if (!script?.src) {
    return '';
  }

  try {
    const srcUrl = new URL(script.src, window.location.origin);
    return srcUrl.searchParams.get('id') || '';
  } catch {
    return '';
  }
}

function resolveMetaPixelIdFromInlineScript() {
  if (typeof document === 'undefined') {
    return '';
  }

  const inlineScripts = Array.from(document.querySelectorAll('script:not([src])'));
  for (const script of inlineScripts) {
    const raw = script.textContent || '';
    const match = raw.match(/fbq\(\s*['"]init['"]\s*,\s*['"]([^'"]+)['"]\s*\)/i);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return '';
}

function getAnalyticsConfig() {
  if (typeof document === 'undefined') {
    return {
      enabled: true,
      gaId: '',
      metaPixelId: '',
    };
  }

  const root = getRoot();
  const body = document.body;
  const analyticsEnabledRaw = body?.dataset?.analyticsEnabled ?? root?.dataset?.analyticsEnabled;
  const analyticsEnabledValue =
    analyticsEnabledRaw == null ? null : String(analyticsEnabledRaw).trim().toLowerCase();

  return {
    enabled:
      analyticsEnabledValue == null
        ? true
        : analyticsEnabledValue !== 'false' &&
          analyticsEnabledValue !== '0' &&
          analyticsEnabledValue !== 'off',
    gaId: body?.dataset?.gaId || root?.dataset?.gaId || resolveGaIdFromScriptTag() || '',
    metaPixelId:
      body?.dataset?.metaPixelId ||
      root?.dataset?.metaPixelId ||
      resolveMetaPixelIdFromInlineScript() ||
      '',
  };
}

function normalizeCategories(categories) {
  return {
    ...DEFAULT_CATEGORIES,
    ...(categories || {}),
    necessary: true,
  };
}

function parseConsent(raw) {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return {
      version: String(parsed.version || ''),
      updatedAt: parsed.updatedAt || '',
      source: parsed.source || 'custom',
      categories: normalizeCategories(parsed.categories),
    };
  } catch {
    return null;
  }
}

function readStoredConsent() {
  const parsed = parseConsent(cookieApi.read(CONSENT_COOKIE));
  if (!parsed) {
    return null;
  }

  if (parsed.version !== getPolicyVersion()) {
    return null;
  }

  return parsed;
}

function writeConsent(categories, source) {
  const consent = {
    version: getPolicyVersion(),
    updatedAt: new Date().toISOString(),
    source,
    categories: normalizeCategories(categories),
  };

  cookieApi.write(CONSENT_COOKIE, JSON.stringify(consent), { maxAge: CONSENT_DURATION });
  return consent;
}

function removeCookieByPrefix(prefix) {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie
    .split(';')
    .map((part) => part.trim())
    .forEach((entry) => {
      if (!entry) return;
      const [name] = entry.split('=', 1);
      if (!name || !name.startsWith(prefix)) return;
      cookieApi.delete(name);
    });
}

function clearDeclinableCookies(categories) {
  if (!categories.analytics) {
    removeCookieByPrefix('_ga');
    removeCookieByPrefix('_cl');
    cookieApi.delete('CLID');
    cookieApi.delete('ANONCHK');
    cookieApi.delete('MR');
    cookieApi.delete('MUID');
    cookieApi.delete('SM');
  }

  if (!categories.targeting) {
    cookieApi.delete('_fbp');
    cookieApi.delete('_fbc');
  }
}

function ensureGtag() {
  if (typeof window === 'undefined') {
    return null;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  return window.gtag;
}

function syncGoogleConsent({ analyticsAllowed, targetingAllowed, gaId }) {
  if (typeof window === 'undefined') {
    return;
  }

  if (gaId) {
    window[`ga-disable-${gaId}`] = !analyticsAllowed;
  }

  const gtag = ensureGtag();
  if (!gtag) {
    return;
  }

  if (!CONSENT_DEFAULTS.google) {
    gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    CONSENT_DEFAULTS.google = true;
  }

  gtag('consent', 'update', {
    ad_storage: targetingAllowed ? 'granted' : 'denied',
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    ad_user_data: targetingAllowed ? 'granted' : 'denied',
    ad_personalization: targetingAllowed ? 'granted' : 'denied',
  });
}

function ensureClarityQueue() {
  if (typeof window === 'undefined') {
    return null;
  }

  window.uetq = window.uetq || [];
  return window.uetq;
}

function syncClarityConsent({ analyticsAllowed }) {
  const uetq = ensureClarityQueue();
  if (!uetq) {
    return;
  }

  if (!CONSENT_DEFAULTS.clarity) {
    uetq.push('consent', 'default', {
      analytics_storage: 'denied',
    });
    CONSENT_DEFAULTS.clarity = true;
  }

  uetq.push('consent', 'update', {
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
  });
}

function ensureFbq() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (typeof window.fbq === 'function') {
    return window.fbq;
  }

  const fbq = function fbqQueue() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
    } else {
      const command = arguments[0];
      const value = arguments[1];

      if (command === 'consent') {
        if (value === 'grant') {
          window.__CATCH_META_TARGETING_ALLOWED = true;
        } else if (value === 'revoke') {
          window.__CATCH_META_TARGETING_ALLOWED = false;
        }
        fbq.queue.push(arguments);
        return;
      }

      if (!window.__CATCH_META_TARGETING_ALLOWED) {
        return;
      }

      fbq.queue.push(arguments);
    }
  };

  if (!window._fbq) {
    window._fbq = fbq;
  }

  fbq.push = fbq;
  fbq.loaded = false;
  fbq.version = '2.0';
  fbq.queue = [];
  if (typeof window.__CATCH_META_TARGETING_ALLOWED !== 'boolean') {
    window.__CATCH_META_TARGETING_ALLOWED = false;
  }
  window.fbq = fbq;
  return fbq;
}

function ensureMetaLibrary(metaPixelId) {
  if (typeof document === 'undefined' || !metaPixelId || SCRIPT_STATE.metaLoaded) {
    return;
  }

  const existing = document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]');
  if (existing) {
    SCRIPT_STATE.metaLoaded = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else if (document.head) {
    document.head.appendChild(script);
  } else {
    document.documentElement.appendChild(script);
  }

  SCRIPT_STATE.metaLoaded = true;
}

function initMetaPixel(metaPixelId) {
  if (!metaPixelId || SCRIPT_STATE.metaInitialized) {
    return;
  }

  const fbq = ensureFbq();
  if (!fbq) {
    return;
  }

  fbq('init', metaPixelId);
  fbq('track', 'PageView');
  initMetaLeaveTracking();
  SCRIPT_STATE.metaInitialized = true;
}

function initMetaLeaveTracking() {
  if (typeof document === 'undefined' || typeof window === 'undefined' || SCRIPT_STATE.metaLeaveBound) {
    return;
  }

  const fbq = ensureFbq();
  if (!fbq) {
    return;
  }

  let leaveTracked = false;
  const trackLeave = () => {
    if (leaveTracked) {
      return;
    }

    leaveTracked = true;
    fbq('trackCustom', 'PageLeave', {
      path: window.location.pathname || '/',
      href: window.location.href || '',
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackLeave();
    }
  });
  window.addEventListener('pagehide', trackLeave);
  window.addEventListener('beforeunload', trackLeave);

  SCRIPT_STATE.metaLeaveBound = true;
}

function syncMetaConsent({ targetingAllowed, metaPixelId }) {
  const fbq = ensureFbq();
  if (!fbq) {
    return;
  }

  if (!CONSENT_DEFAULTS.meta) {
    fbq('consent', 'revoke');
    CONSENT_DEFAULTS.meta = true;
  }

  if (targetingAllowed) {
    ensureMetaLibrary(metaPixelId);
    fbq('consent', 'grant');
    initMetaPixel(metaPixelId);
    return;
  }

  fbq('consent', 'revoke');
}

function applyConsentEffects(consent, { onTelemetryChange } = {}) {
  const config = getAnalyticsConfig();
  const categories = normalizeCategories(consent?.categories);
  const analyticsAllowed = Boolean(config.enabled && categories.analytics);
  const targetingAllowed = Boolean(config.enabled && categories.targeting);

  cookieApi.init({ analyticsAllowed });

  syncGoogleConsent({ analyticsAllowed, targetingAllowed, gaId: config.gaId });
  syncClarityConsent({ analyticsAllowed });
  syncMetaConsent({ targetingAllowed, metaPixelId: config.metaPixelId });

  clearDeclinableCookies(categories);

  if (typeof onTelemetryChange === 'function') {
    onTelemetryChange(analyticsAllowed);
  }
}

function setToggleValues(modal, categories) {
  if (!modal) return;

  const analyticsToggle = modal.querySelector('[data-consent-toggle="analytics"]');
  const targetingToggle = modal.querySelector('[data-consent-toggle="targeting"]');
  const functionalToggle = modal.querySelector('[data-consent-toggle="functional"]');

  if (functionalToggle) functionalToggle.checked = Boolean(categories.functional);
  if (analyticsToggle) analyticsToggle.checked = Boolean(categories.analytics);
  if (targetingToggle) targetingToggle.checked = Boolean(categories.targeting);
}

function readToggleValues(modal) {
  if (!modal) {
    return { ...DEFAULT_CATEGORIES };
  }

  const read = (name, fallback) => {
    const field = modal.querySelector(`[data-consent-toggle="${name}"]`);
    return field ? Boolean(field.checked) : fallback;
  };

  return {
    necessary: true,
    functional: read('functional', DEFAULT_CATEGORIES.functional),
    analytics: read('analytics', DEFAULT_CATEGORIES.analytics),
    targeting: read('targeting', DEFAULT_CATEGORIES.targeting),
  };
}

function setLocked(locked) {
  if (typeof document === 'undefined') return;

  if (locked) {
    document.body.classList.add('consent-locked');
  } else {
    document.body.classList.remove('consent-locked');
  }
}

export function initCookieConsent({ onConsentResolved, onTelemetryChange } = {}) {
  if (typeof document === 'undefined') {
    return { consent: null };
  }

  const banner = document.getElementById('cookieConsentBanner');
  const modalOverlay = document.getElementById('cookieSettingsOverlay');
  if (!banner || !modalOverlay) {
    return { consent: null };
  }

  if (isEmbedMode()) {
    banner.classList.remove('is-open');
    modalOverlay.classList.remove('is-open');
    setLocked(false);
    return { consent: null };
  }

  const saveButton = modalOverlay.querySelector('[data-consent-action="save"]');
  const acceptAllButton = banner.querySelector('[data-consent-action="accept-all"]');
  const rejectOptionalButton = banner.querySelector('[data-consent-action="reject-optional"]');
  const openSettingsButtons = document.querySelectorAll('[data-consent-action="open-settings"]');
  const closeSettingsButton = modalOverlay.querySelector('[data-consent-action="close-settings"]');

  let consent = readStoredConsent();
  let canCloseSettings = Boolean(consent);

  const closeModal = () => {
    if (!canCloseSettings) {
      return;
    }
    modalOverlay.classList.remove('is-open');
  };

  const openModal = () => {
    const categories = normalizeCategories(consent?.categories);
    setToggleValues(modalOverlay, categories);
    modalOverlay.classList.add('is-open');
  };

  const finalize = (nextConsent) => {
    consent = nextConsent;
    canCloseSettings = true;
    banner.classList.remove('is-open');
    setLocked(false);
    applyConsentEffects(consent, { onTelemetryChange });
    if (typeof onConsentResolved === 'function') {
      onConsentResolved(consent);
    }
  };

  const saveConsent = (categories, source) => {
    const nextConsent = writeConsent(categories, source);
    finalize(nextConsent);
    modalOverlay.classList.remove('is-open');
  };

  acceptAllButton?.addEventListener('click', () => {
    saveConsent(
      {
        necessary: true,
        functional: true,
        analytics: true,
        targeting: true,
      },
      'accept_all'
    );
  });

  rejectOptionalButton?.addEventListener('click', () => {
    saveConsent(
      {
        necessary: true,
        functional: false,
        analytics: false,
        targeting: false,
      },
      'reject_optional'
    );
  });

  openSettingsButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      openModal();
    });
  });

  closeSettingsButton?.addEventListener('click', closeModal);
  modalOverlay.addEventListener('click', (event) => {
    if (event.target === modalOverlay) {
      closeModal();
    }
  });

  saveButton?.addEventListener('click', () => {
    const categories = readToggleValues(modalOverlay);
    saveConsent(categories, 'custom');
  });

  if (!consent) {
    banner.classList.add('is-open');
    setLocked(true);
    modalOverlay.classList.remove('is-open');
    applyConsentEffects(
      {
        categories: {
          ...DEFAULT_CATEGORIES,
          analytics: false,
          targeting: false,
        },
      },
      { onTelemetryChange }
    );
  } else {
    banner.classList.remove('is-open');
    setLocked(false);
    applyConsentEffects(consent, { onTelemetryChange });
    if (typeof onConsentResolved === 'function') {
      onConsentResolved(consent);
    }
  }

  return {
    getConsent: () => consent,
    openSettings: openModal,
  };
}

export default {
  init: initCookieConsent,
};
